import React, { useRef, useState } from 'react';
import { z } from 'zod';
import { LeftOutlined } from '@ant-design/icons';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedForm from '@portal/components/AdvancedForm/AdvancedForm';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import RecaptchaCheckbox, {
  RecaptchaCheckboxHandle,
} from '@portal/components/RecaptchaCheckbox/RecaptchaCheckbox';
import { getRouteStackPath } from '@portal/config/routes';
import { useAppIntegrityRequired } from '@portal/hooks/useAppIntegrityRequired';
import {
  removeHeader,
  setHeader,
} from '@portal/repositories/securitySdkConfig';
import { TaxIdentifierType } from '@portal/enum/onboardingType';
import ErrorsMessages from '@portal/enum/errors';
import { validateStatus } from '@portal/enum/validateStatus';
import { useNavigation } from '@portal/hooks/useNavigation';
import OnboardingApi from '@portal/repositories/onboarding';
import { translate } from '@portal/services/i18n';
import { isBcProtegeEnabledForThisPerson } from '@portal/utils/bcProtege';
import {
  isCNPJ,
  isCpf,
  maskCpfOrCnpj,
  unmaskField,
} from '@portal/services/masks';

import * as MessageService from '~/services/message';
import StorageService from '@portal/services/storage';

import './Register.less';

const taxIdentifierSchema = z
  .string()
  .refine((digits) => isCpf(digits) || isCNPJ(digits));

const Register = () => {
  const navigate = useNavigation<{
    taxIdentifier?: string;
    accountType?: TaxIdentifierType;
  }>();
  const [taxId, setTaxId] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const recaptchaRequired = useAppIntegrityRequired();
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<RecaptchaCheckboxHandle>(null);

  const cleanTaxId = unmaskField(taxId);
  const isValidTaxId = taxIdentifierSchema.safeParse(cleanTaxId).success;
  const showInlineError = cleanTaxId.length > 0 && !isValidTaxId;
  const inlineErrorMessage = showInlineError
    ? translate('APPLICATION.ERRORS.INVALID_TAX_IDENTIFIER')
    : null;

  const handleChange = (raw: string) => {
    if (inputError) setInputError(null);
    setTaxId(maskCpfOrCnpj(raw));
  };

  const handleRecaptchaVerify = (token: string) => {
    setRecaptchaToken(token);
    setHeader('x-recaptcha-token', token);
  };

  const handleRecaptchaExpire = () => {
    setRecaptchaToken(null);
    removeHeader('x-recaptcha-token');
  };

  const resetRecaptcha = () => {
    if (!recaptchaRequired) return;
    recaptchaRef.current?.reset();
    setRecaptchaToken(null);
    removeHeader('x-recaptcha-token');
  };

  const navigateAfterValidation = async (cleanTaxId: string) => {
    const useBcProtege = await isBcProtegeEnabledForThisPerson(cleanTaxId);
    const accountType =
      cleanTaxId.length === 14 ? TaxIdentifierType.PJ : TaxIdentifierType.PF;

    StorageService.setSessionItem(
      StorageService.StorageItems.ONBOARDING_TAX_ID,
      JSON.stringify({ taxIdentifier: cleanTaxId, accountType })
    );

    const nextRouteId = useBcProtege ? 'BC_PROTEGE_VALIDATION' : 'OPEN_ACCOUNT';
    navigate(getRouteStackPath('AUTH', nextRouteId), {
      state: { taxIdentifier: cleanTaxId, accountType },
    });
  };

  const handleSubmit = async () => {
    if (!isValidTaxId || submitting) return;

    if (recaptchaRequired && !recaptchaToken) {
      MessageService.error('RECAPTCHA_REQUIRED');
      return;
    }

    setInputError(null);
    setSubmitting(true);
    const cleanTaxId = unmaskField(taxId);

    try {
      const { status } = await OnboardingApi.validateTaxId(cleanTaxId);

      if (status === validateStatus.IN_USE) {
        setInputError(translate('PAGES.AUTH.REGISTER.TAX_ID.ERROR_IN_USE'));
        return;
      }

      if (status === ErrorsMessages.RESTRICTED_LIST) {
        MessageService.error(
          translate('PAGES.AUTH.REGISTER.TAX_ID.ERROR_RESTRICTED')
        );
        return;
      }

      if (status === ErrorsMessages.NOT_ALLOWED) {
        MessageService.error(
          translate('PAGES.AUTH.REGISTER.TAX_ID.ERROR_NOT_ALLOWED')
        );
        return;
      }

      if (status !== validateStatus.OK) {
        MessageService.error(
          translate('PAGES.AUTH.REGISTER.TAX_ID.ERROR_GENERIC')
        );
        return;
      }

      await navigateAfterValidation(cleanTaxId);
    } catch (err) {
      const originalError: Error | undefined =
        err instanceof Error ? err : undefined;

      if (
        originalError?.message === ErrorsMessages.MISSING_RECAPTCHA_TOKEN ||
        originalError?.message === ErrorsMessages.INVALID_RECAPTCHA_TOKEN
      ) {
        MessageService.error('RECAPTCHA_REQUIRED');
        return;
      }

      if (originalError?.message === ErrorsMessages.INVALID_TAX_IDENTIFIER) {
        setInputError(translate('APPLICATION.ERRORS.INVALID_TAX_IDENTIFIER'));
        return;
      }

      MessageService.error(
        translate('PAGES.AUTH.REGISTER.TAX_ID.ERROR_GENERIC'),
        { originalError }
      );
    } finally {
      resetRecaptcha();
      setSubmitting(false);
    }
  };

  const inputLabel = translate('PAGES.AUTH.REGISTER.TAX_ID.INPUT_LABEL');
  const placeholder = translate('PAGES.AUTH.REGISTER.TAX_ID.INPUT_PLACEHOLDER');
  const title = translate('PAGES.AUTH.REGISTER.TAX_ID.TITLE');
  const subtitle = translate('PAGES.AUTH.REGISTER.TAX_ID.SUBTITLE');
  const inputHelperText = translate('PAGES.AUTH.REGISTER.TAX_ID.INPUT_HELPER');

  return (
    <div className="register">
      <div className="register__card">
        <button
          type="button"
          className="register__back"
          onClick={() => navigate('/')}
        >
          <LeftOutlined />
          {translate('PAGES.AUTH.REGISTER.TAX_ID.BACK')}
        </button>
        <header className="register__header">
          <h1 className="register__title">{title}</h1>
          <p className="register__subtitle">{subtitle}</p>
        </header>
        <AdvancedForm onSubmit={handleSubmit} className="register__form">
          <AdvancedInput
            label={inputLabel}
            placeholder={placeholder}
            value={taxId}
            onChange={handleChange}
            helperText={
              inputError || inlineErrorMessage ? undefined : inputHelperText
            }
            error={Boolean(inputError) || showInlineError}
            errorMessage={inputError ?? inlineErrorMessage ?? undefined}
            autoFocus
            testId="register-tax-id"
          />
          {!!recaptchaRequired && (
            <div className="register__recaptcha">
              <RecaptchaCheckbox
                ref={recaptchaRef}
                action="ONBOARDING"
                onVerify={handleRecaptchaVerify}
                onExpire={handleRecaptchaExpire}
              />
            </div>
          )}
          <div className="register__submit">
            <AdvancedButton
              text={translate('PAGES.AUTH.REGISTER.TAX_ID.CONTINUE')}
              type="submit"
              fullWidth
              disabled={
                !isValidTaxId ||
                submitting ||
                (recaptchaRequired && !recaptchaToken)
              }
              loading={submitting}
              testId="register-continue"
            />
          </div>
        </AdvancedForm>
      </div>
    </div>
  );
};

export default Register;
